import { type AccountError, DrawingClient, type DrawingClientError } from '@acies/platform';
import { Cache, Effect } from 'effect';
import { type ApiServices, runCall } from './runtime';
import { callDependencies, type DrawingPath, keepAnswers } from './shared';

/**
 * How many drawings to keep.
 *
 * A cap, because Effect caches take one, and here it is a number worth choosing: a drawing is some 40 kB of markup, and a bill of materials can hold dozens of positions. Thirty-two is more than anyone looks at in a sitting and still bounded well under a couple of megabytes.
 */
const DRAWING_CAPACITY = 32;

/** Everything asking the CDN for a drawing can fail with. */
export type GetDrawingError = AccountError | DrawingClientError;

/** The calls against the CDN the drawings of priced positions are served from. */
export interface DrawingCalls {
	/**
	 * Reads the drawing one calculation left behind, as SVG markup.
	 *
	 * Takes the drawing's id rather than the position it belongs to, because whether a position has a drawing at all is the position's own business — see `ModelPosition.drawingId`, which is where that question is answered. By the time a call gets here there is a drawing.
	 *
	 * The markup itself rather than a URL, so the caller can put it in the document and style it with the rest of the page. It is answered as text and nothing more: no parsing, no schema. An SVG is not a shape we have an opinion about, and pretending otherwise would only mean failing a drawing over a detail nobody
	 * reads.
	 */
	readonly getDrawing: (drawingId: string) => Effect.Effect<string, GetDrawingError>;
}

/** Reads the markup at one path, which is the whole of what fetching a drawing is. */
function markupAt(client: typeof DrawingClient.Service, path: string): Effect.Effect<string, DrawingClientError> {
	return Effect.gen(function* () {
		const response = yield* client.get(path);
		return yield* response.text;
	});
}

/** Implements the drawing calls over the CDN client and the path the account names. */
export function makeDrawingCalls(client: typeof DrawingClient.Service, drawingPath: DrawingPath): DrawingCalls {
	return {
		getDrawing: (drawingId) => drawingPath(drawingId).pipe(Effect.flatMap((path) => markupAt(client, path))),
	};
}

/**
 * The drawings the CDN has served, kept for as long as the page lives.
 *
 * The most cacheable thing the Configurator asks for. A drawing is named after the calculation it was made for, and a calculation that succeeded never changes — so an answer is not merely worth keeping, it can be kept without any rule for when to let it go. Selecting back and forth between two positions is what this
 * is for, and that is what people do.
 *
 * The markup and not the parsed element, and that is the point of caching here rather than one layer up. A DOM node can only be in one place: two viewers showing the same drawing would pull the same node between them, and a node adopted into a shadow root and taken out again is a shared, mutated thing. A string is a
 * value. `parseDrawing` makes a fresh element from it every time, which is what a viewer wants.
 *
 * Keyed by the path, which names the account and its engine as well as the calculation — so a drawing cannot outlive the account it belongs to, the same way the model searches cannot outlive the store they were made in.
 */
const drawings = Effect.runSync(
	Cache.makeWith(
		(path: string) =>
			Effect.gen(function* () {
				const client = yield* DrawingClient;
				return yield* markupAt(client, path);
			}),
		{ capacity: DRAWING_CAPACITY, requireServicesAt: 'lookup', timeToLive: keepAnswers },
	),
);

/**
 * Forgets every drawing that has been read.
 *
 * Nothing in the Configurator calls this: a drawing cannot change, which is the whole reason it is kept. Tests do, because a cache that outlives what put things in it would answer one test with what another one arranged.
 */
export const forgetDrawings = (): Promise<void> => runCall(() => Cache.invalidateAll(drawings));

/**
 * Reads one drawing, from outside Effect.
 *
 * The awaitable form of {@link DrawingCalls.getDrawing}, for the components that show a drawing. A failure comes back as itself rather than as a bare `false` — see {@link runCall} — because a drawing that could not be fetched and a position that has none are different things for a section to say. An answer is the SVG
 * markup, so a caller tells the two apart by what an answer is: a string, where every failure is a tagged class.
 */
export const getDrawing = (drawingId: string): Promise<GetDrawingError | string> => runCall(() => getDrawingEffect(drawingId));

/**
 * Reads one drawing, as an Effect over the Configurator's services.
 *
 * For a caller that forks the read rather than awaiting it, so a drawing no longer wanted can be interrupted — see `ConfiguratorMachine`.
 */
export const getDrawingEffect = (drawingId: string): Effect.Effect<string, GetDrawingError, ApiServices> => callDependencies.pipe(Effect.flatMap(({ drawingPath }) => drawingPath(drawingId).pipe(Effect.flatMap((path) => Cache.get(drawings, path)))));
